/**
 * BlockedIPsTablePage - Active IP blocks management
 *
 * Filtered view of GeoLocatedIP records where is_blocked=true.
 * Provides unblock and whitelist actions from the table.
 */

import TablePage from '@core/pages/TablePage.js';
import { GeoLocatedIP, GeoLocatedIPList } from '@core/models/System.js';
import GeoIPView from '../account/devices/GeoIPView.js';

// Idempotent — GeoLocatedIPTablePage also registers this static. Setting
// it here too means BlockedIPsTablePage works when imported directly,
// not just via admin/index.js's eager-load order.
GeoLocatedIP.VIEW_CLASS = GeoIPView;

export default class BlockedIPsTablePage extends TablePage {
    constructor(options = {}) {
        super({
            ...options,
            name: 'admin_blocked_ips',
            pageName: 'Blocked IPs',
            router: 'admin/security/blocked-ips',
            Collection: GeoLocatedIPList,

            dayRangeFilter: { field: 'blocked_at', value: '7d' },
            searchPlaceholder: 'Search IP, country, or rule',

            viewDialogOptions: {
                header: false,
                size: 'xl',
                noBodyPadding: true,
                buttons: []
            },

            defaultQuery: {
                sort: '-modified',
                // String 'true' matches GeoLocatedIPTablePage's boolean filter
                // wire format — keeps the URL stable across reloads (URL sync
                // serializes boolean `true` as 'true' anyway, so pre-stringing
                // avoids the first-paint desync).
                is_blocked: 'true',
            },

            columns: [
                {
                    key: 'ip_address',
                    label: 'IP Address',
                    sortable: true,
                    template: '<code>{{model.ip_address}}</code>'
                },
                {
                    key: 'threat_level',
                    label: 'Threat Level',
                    sortable: true,
                    filter: {
                        type: 'select',
                        options: ['none', 'low', 'medium', 'high', 'critical']
                    }
                },
                {
                    key: 'country_code',
                    label: 'Country',
                    sortable: true,
                    filter: { type: 'text' }
                },
                {
                    key: 'city',
                    label: 'City',
                    sortable: true
                },
                {
                    key: 'blocked_reason',
                    label: 'Reason',
                    formatter: 'truncate(40)'
                },
                {
                    key: 'blocked_at',
                    label: 'Blocked At',
                    formatter: 'datetime',
                    sortable: true
                },
                {
                    key: 'blocked_until',
                    label: 'Expires',
                    formatter: 'datetime',
                    sortable: true
                }
            ],

            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,
            selectable: true,

            showRefresh: true,
            showAdd: false,
            showExport: true,

            emptyMessage: 'No blocked IPs. The firewall has no active IP blocks.',

            batchBarLocation: 'top',
            batchActions: [
                { label: 'Unblock', icon: 'bi bi-unlock', action: 'unblock' },
                { label: 'Whitelist', icon: 'bi bi-check-circle', action: 'whitelist' },
            ],

            tableOptions: {
                striped: true,
                bordered: false,
                hover: true,
                responsive: false
            }
        });
    }

    onActionBatchUnblock() {
        return this.batchAction({ field: 'unblock',   value: 'Bulk unblock from admin',   label: 'Unblock' });
    }

    onActionBatchWhitelist() {
        return this.batchAction({ field: 'whitelist', value: 'Bulk whitelist from admin', label: 'Whitelist' });
    }
}
